/**
 * Schema type definitions and form field configurations
 * for the JSON-LD Schema Generator tool.
 */

export interface SchemaField {
  key: string;
  label: string;
  type: "text" | "textarea" | "url" | "number" | "date" | "select" | "array";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  arrayItemLabel?: string;
  arrayFields?: SchemaField[];
}

export interface SchemaTypeConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  fields: SchemaField[];
  buildJsonLd: (data: Record<string, unknown>) => Record<string, unknown>;
  validationRules?: { field: string; message: string }[];
}

// --- Builder functions ---

function buildOrganization(data: Record<string, unknown>) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: data.name || "",
    url: data.url || "",
  };
  if (data.logo) schema.logo = data.logo;
  if (data.description) schema.description = data.description;
  if (data.telephone) schema.telephone = data.telephone;
  if (data.email) schema.email = data.email;
  if (data.address_street || data.address_city) {
    schema.address = {
      "@type": "PostalAddress",
      streetAddress: data.address_street || "",
      addressLocality: data.address_city || "",
      addressRegion: data.address_state || "",
      postalCode: data.address_zip || "",
      addressCountry: data.address_country || "US",
    };
  }
  const sameAs: string[] = [];
  if (data.facebook) sameAs.push(data.facebook as string);
  if (data.twitter) sameAs.push(data.twitter as string);
  if (data.linkedin) sameAs.push(data.linkedin as string);
  if (data.instagram) sameAs.push(data.instagram as string);
  if (sameAs.length > 0) schema.sameAs = sameAs;
  return schema;
}

function buildArticle(data: Record<string, unknown>) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": data.articleType || "Article",
    headline: data.headline || "",
    datePublished: data.datePublished || "",
  };
  if (data.dateModified) schema.dateModified = data.dateModified;
  if (data.authorName) {
    schema.author = { "@type": "Person", name: data.authorName };
    if (data.authorUrl) (schema.author as Record<string, unknown>).url = data.authorUrl;
  }
  if (data.publisherName) {
    schema.publisher = {
      "@type": "Organization",
      name: data.publisherName,
    };
    if (data.publisherLogo) {
      (schema.publisher as Record<string, unknown>).logo = {
        "@type": "ImageObject",
        url: data.publisherLogo,
      };
    }
  }
  if (data.image) schema.image = data.image;
  if (data.description) schema.description = data.description;
  if (data.url) schema.mainEntityOfPage = { "@type": "WebPage", "@id": data.url };
  return schema;
}

function buildProduct(data: Record<string, unknown>) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: data.name || "",
  };
  if (data.description) schema.description = data.description;
  if (data.image) schema.image = data.image;
  if (data.brand) schema.brand = { "@type": "Brand", name: data.brand };
  if (data.sku) schema.sku = data.sku;
  if (data.price) {
    schema.offers = {
      "@type": "Offer",
      price: data.price,
      priceCurrency: data.currency || "USD",
      availability: data.availability || "https://schema.org/InStock",
    };
    if (data.url) (schema.offers as Record<string, unknown>).url = data.url;
  }
  if (data.ratingValue) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: data.ratingValue,
      reviewCount: data.reviewCount || "1",
    };
  }
  return schema;
}

function buildFAQ(data: Record<string, unknown>) {
  const items = (data.faqItems as Array<{ question: string; answer: string }>) || [];
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items
      .filter((item) => item.question && item.answer)
      .map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
  };
}

function buildHowTo(data: Record<string, unknown>) {
  const steps = (data.steps as Array<{ name: string; text: string }>) || [];
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: data.name || "",
    step: steps
      .filter((s) => s.name && s.text)
      .map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
  };
  if (data.description) schema.description = data.description;
  if (data.totalTime) schema.totalTime = data.totalTime;
  if (data.image) schema.image = data.image;
  return schema;
}

function buildLocalBusiness(data: Record<string, unknown>) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": data.businessType || "LocalBusiness",
    name: data.name || "",
  };
  if (data.description) schema.description = data.description;
  if (data.image) schema.image = data.image;
  if (data.telephone) schema.telephone = data.telephone;
  if (data.url) schema.url = data.url;
  if (data.priceRange) schema.priceRange = data.priceRange;
  schema.address = {
    "@type": "PostalAddress",
    streetAddress: data.address_street || "",
    addressLocality: data.address_city || "",
    addressRegion: data.address_state || "",
    postalCode: data.address_zip || "",
    addressCountry: data.address_country || "US",
  };
  if (data.latitude && data.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: data.latitude,
      longitude: data.longitude,
    };
  }
  if (data.openingHours) schema.openingHoursSpecification = data.openingHours;
  if (data.ratingValue) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: data.ratingValue,
      reviewCount: data.reviewCount || "1",
    };
  }
  return schema;
}

function buildEvent(data: Record<string, unknown>) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: data.name || "",
    startDate: data.startDate || "",
  };
  if (data.endDate) schema.endDate = data.endDate;
  if (data.description) schema.description = data.description;
  if (data.image) schema.image = data.image;
  if (data.url) schema.url = data.url;
  if (data.locationName) {
    schema.location = {
      "@type": "Place",
      name: data.locationName,
      address: {
        "@type": "PostalAddress",
        streetAddress: data.locationAddress || "",
        addressLocality: data.locationCity || "",
        addressRegion: data.locationState || "",
        postalCode: data.locationZip || "",
        addressCountry: data.locationCountry || "US",
      },
    };
  }
  if (data.organizerName) {
    schema.organizer = {
      "@type": "Organization",
      name: data.organizerName,
      url: (data.organizerUrl as string) || "",
    };
  }
  if (data.price) {
    schema.offers = {
      "@type": "Offer",
      price: data.price,
      priceCurrency: data.currency || "USD",
      availability: data.availability || "https://schema.org/InStock",
      url: data.ticketUrl || "",
    };
  }
  return schema;
}

function buildBreadcrumbs(data: Record<string, unknown>) {
  const items = (data.breadcrumbs as Array<{ name: string; url: string }>) || [];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items
      .filter((item) => item.name)
      .map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        item: item.url || undefined,
      })),
  };
}

function buildJobPosting(data: Record<string, unknown>) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: data.title || "",
    description: data.description || "",
    datePosted: data.datePosted || "",
    hiringOrganization: {
      "@type": "Organization",
      name: data.companyName || "",
      sameAs: data.companyUrl || "",
    },
  };
  if (data.validThrough) schema.validThrough = data.validThrough;
  if (data.employmentType) schema.employmentType = data.employmentType;
  if (data.locationCity) {
    schema.jobLocation = {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: data.locationCity,
        addressRegion: data.locationState || "",
        addressCountry: data.locationCountry || "US",
      },
    };
  }
  if (data.remote === "true") {
    schema.jobLocationType = "TELECOMMUTE";
  }
  if (data.salaryMin || data.salaryMax) {
    schema.baseSalary = {
      "@type": "MonetaryAmount",
      currency: data.salaryCurrency || "USD",
      value: {
        "@type": "QuantitativeValue",
        minValue: data.salaryMin || undefined,
        maxValue: data.salaryMax || undefined,
        unitText: data.salaryUnit || "YEAR",
      },
    };
  }
  return schema;
}

function buildService(data: Record<string, unknown>) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.name || "",
  };
  if (data.description) schema.description = data.description;
  if (data.url) schema.url = data.url;
  if (data.providerName) {
    schema.provider = {
      "@type": data.providerType || "Organization",
      name: data.providerName,
      url: data.providerUrl || "",
    };
  }
  if (data.serviceType) schema.serviceType = data.serviceType;
  if (data.areaServed) schema.areaServed = data.areaServed;
  if (data.price) {
    schema.offers = {
      "@type": "Offer",
      price: data.price,
      priceCurrency: data.currency || "USD",
    };
  }
  if (data.ratingValue) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: data.ratingValue,
      reviewCount: data.reviewCount || "1",
    };
  }
  return schema;
}

// --- Schema type configurations ---

export const SCHEMA_TYPES: SchemaTypeConfig[] = [
  {
    id: "organization",
    name: "Organization",
    description: "Company or organization info",
    icon: "Building2",
    color: "text-orange-600 bg-orange-100",
    fields: [
      { key: "name", label: "Organization Name", type: "text", required: true, placeholder: "Acme Corp" },
      { key: "url", label: "Website URL", type: "url", required: true, placeholder: "https://example.com" },
      { key: "logo", label: "Logo URL", type: "url", placeholder: "https://example.com/logo.png" },
      { key: "description", label: "Description", type: "textarea", placeholder: "Brief description of your organization" },
      { key: "telephone", label: "Phone Number", type: "text", placeholder: "+1-555-555-5555" },
      { key: "email", label: "Email", type: "text", placeholder: "info@example.com" },
      { key: "address_street", label: "Street Address", type: "text", placeholder: "123 Main St" },
      { key: "address_city", label: "City", type: "text", placeholder: "New York" },
      { key: "address_state", label: "State/Region", type: "text", placeholder: "NY" },
      { key: "address_zip", label: "Postal Code", type: "text", placeholder: "10001" },
      { key: "address_country", label: "Country", type: "text", placeholder: "US" },
      { key: "facebook", label: "Facebook URL", type: "url", placeholder: "https://facebook.com/yourpage" },
      { key: "twitter", label: "Twitter URL", type: "url", placeholder: "https://twitter.com/yourhandle" },
      { key: "linkedin", label: "LinkedIn URL", type: "url", placeholder: "https://linkedin.com/company/yours" },
      { key: "instagram", label: "Instagram URL", type: "url", placeholder: "https://instagram.com/yourhandle" },
    ],
    buildJsonLd: buildOrganization,
  },
  {
    id: "article",
    name: "Article",
    description: "Blog posts and news articles",
    icon: "FileText",
    color: "text-blue-600 bg-blue-100",
    fields: [
      {
        key: "articleType", label: "Article Type", type: "select", required: true,
        options: [
          { value: "Article", label: "Article" },
          { value: "NewsArticle", label: "News Article" },
          { value: "BlogPosting", label: "Blog Post" },
        ],
      },
      { key: "headline", label: "Headline", type: "text", required: true, placeholder: "Your article headline" },
      { key: "description", label: "Description", type: "textarea", placeholder: "Brief article description" },
      { key: "url", label: "Article URL", type: "url", placeholder: "https://example.com/article" },
      { key: "image", label: "Image URL", type: "url", placeholder: "https://example.com/image.jpg" },
      { key: "datePublished", label: "Date Published", type: "date", required: true },
      { key: "dateModified", label: "Date Modified", type: "date" },
      { key: "authorName", label: "Author Name", type: "text", required: true, placeholder: "John Doe" },
      { key: "authorUrl", label: "Author URL", type: "url", placeholder: "https://example.com/author" },
      { key: "publisherName", label: "Publisher Name", type: "text", required: true, placeholder: "Example News" },
      { key: "publisherLogo", label: "Publisher Logo URL", type: "url", placeholder: "https://example.com/logo.png" },
    ],
    buildJsonLd: buildArticle,
  },
  {
    id: "product",
    name: "Product",
    description: "Products with price & reviews",
    icon: "ShoppingBag",
    color: "text-green-600 bg-green-100",
    fields: [
      { key: "name", label: "Product Name", type: "text", required: true, placeholder: "Product Name" },
      { key: "description", label: "Description", type: "textarea", placeholder: "Product description" },
      { key: "image", label: "Image URL", type: "url", placeholder: "https://example.com/product.jpg" },
      { key: "brand", label: "Brand", type: "text", placeholder: "Brand Name" },
      { key: "sku", label: "SKU", type: "text", placeholder: "SKU-12345" },
      { key: "url", label: "Product URL", type: "url", placeholder: "https://example.com/product" },
      { key: "price", label: "Price", type: "number", required: true, placeholder: "29.99" },
      { key: "currency", label: "Currency", type: "text", placeholder: "USD" },
      {
        key: "availability", label: "Availability", type: "select",
        options: [
          { value: "https://schema.org/InStock", label: "In Stock" },
          { value: "https://schema.org/OutOfStock", label: "Out of Stock" },
          { value: "https://schema.org/PreOrder", label: "Pre-Order" },
        ],
      },
      { key: "ratingValue", label: "Rating (1-5)", type: "number", placeholder: "4.5" },
      { key: "reviewCount", label: "Review Count", type: "number", placeholder: "120" },
    ],
    buildJsonLd: buildProduct,
  },
  {
    id: "faq",
    name: "FAQ",
    description: "Frequently asked questions",
    icon: "HelpCircle",
    color: "text-coral bg-coral-light",
    fields: [
      {
        key: "faqItems", label: "FAQ Items", type: "array",
        arrayItemLabel: "Question",
        arrayFields: [
          { key: "question", label: "Question", type: "text", required: true, placeholder: "What is your return policy?" },
          { key: "answer", label: "Answer", type: "textarea", required: true, placeholder: "Our return policy allows..." },
        ],
      },
    ],
    buildJsonLd: buildFAQ,
  },
  {
    id: "howto",
    name: "How-To",
    description: "Step-by-step instructions",
    icon: "ListOrdered",
    color: "text-teal bg-teal/10",
    fields: [
      { key: "name", label: "Title", type: "text", required: true, placeholder: "How to change a tire" },
      { key: "description", label: "Description", type: "textarea", placeholder: "A step-by-step guide to..." },
      { key: "image", label: "Image URL", type: "url", placeholder: "https://example.com/howto.jpg" },
      { key: "totalTime", label: "Total Time (ISO 8601)", type: "text", placeholder: "PT30M" },
      {
        key: "steps", label: "Steps", type: "array",
        arrayItemLabel: "Step",
        arrayFields: [
          { key: "name", label: "Step Name", type: "text", required: true, placeholder: "Loosen the lug nuts" },
          { key: "text", label: "Step Description", type: "textarea", required: true, placeholder: "Using the wrench, loosen each lug nut..." },
        ],
      },
    ],
    buildJsonLd: buildHowTo,
  },
  {
    id: "localbusiness",
    name: "Local Business",
    description: "Physical business location",
    icon: "MapPin",
    color: "text-red-600 bg-red-100",
    fields: [
      {
        key: "businessType", label: "Business Type", type: "select",
        options: [
          { value: "LocalBusiness", label: "Local Business (General)" },
          { value: "Restaurant", label: "Restaurant" },
          { value: "Store", label: "Store" },
          { value: "MedicalBusiness", label: "Medical Business" },
          { value: "LegalService", label: "Legal Service" },
          { value: "FinancialService", label: "Financial Service" },
          { value: "HomeAndConstructionBusiness", label: "Home & Construction" },
          { value: "AutomotiveBusiness", label: "Automotive Business" },
        ],
      },
      { key: "name", label: "Business Name", type: "text", required: true, placeholder: "Joe's Pizza" },
      { key: "description", label: "Description", type: "textarea", placeholder: "Description of your business" },
      { key: "url", label: "Website URL", type: "url", placeholder: "https://example.com" },
      { key: "telephone", label: "Phone", type: "text", placeholder: "+1-555-555-5555" },
      { key: "image", label: "Image URL", type: "url", placeholder: "https://example.com/photo.jpg" },
      { key: "priceRange", label: "Price Range", type: "text", placeholder: "$$" },
      { key: "address_street", label: "Street Address", type: "text", required: true, placeholder: "123 Main St" },
      { key: "address_city", label: "City", type: "text", required: true, placeholder: "New York" },
      { key: "address_state", label: "State/Region", type: "text", placeholder: "NY" },
      { key: "address_zip", label: "Postal Code", type: "text", placeholder: "10001" },
      { key: "address_country", label: "Country", type: "text", placeholder: "US" },
      { key: "latitude", label: "Latitude", type: "text", placeholder: "40.7128" },
      { key: "longitude", label: "Longitude", type: "text", placeholder: "-74.0060" },
      { key: "ratingValue", label: "Rating (1-5)", type: "number", placeholder: "4.5" },
      { key: "reviewCount", label: "Review Count", type: "number", placeholder: "120" },
    ],
    buildJsonLd: buildLocalBusiness,
  },
  {
    id: "event",
    name: "Event",
    description: "Events and conferences",
    icon: "CalendarDays",
    color: "text-pink-600 bg-pink-100",
    fields: [
      { key: "name", label: "Event Name", type: "text", required: true, placeholder: "Annual Tech Conference" },
      { key: "description", label: "Description", type: "textarea", placeholder: "Event description" },
      { key: "startDate", label: "Start Date/Time", type: "date", required: true },
      { key: "endDate", label: "End Date/Time", type: "date" },
      { key: "url", label: "Event URL", type: "url", placeholder: "https://example.com/event" },
      { key: "image", label: "Image URL", type: "url", placeholder: "https://example.com/event.jpg" },
      { key: "locationName", label: "Venue Name", type: "text", required: true, placeholder: "Convention Center" },
      { key: "locationAddress", label: "Venue Street", type: "text", placeholder: "123 Main St" },
      { key: "locationCity", label: "Venue City", type: "text", placeholder: "San Francisco" },
      { key: "locationState", label: "Venue State", type: "text", placeholder: "CA" },
      { key: "locationZip", label: "Venue Zip", type: "text", placeholder: "94102" },
      { key: "locationCountry", label: "Venue Country", type: "text", placeholder: "US" },
      { key: "organizerName", label: "Organizer Name", type: "text", placeholder: "Tech Events Inc." },
      { key: "organizerUrl", label: "Organizer URL", type: "url", placeholder: "https://example.com" },
      { key: "price", label: "Ticket Price", type: "number", placeholder: "99.00" },
      { key: "currency", label: "Currency", type: "text", placeholder: "USD" },
      { key: "ticketUrl", label: "Ticket URL", type: "url", placeholder: "https://example.com/tickets" },
    ],
    buildJsonLd: buildEvent,
  },
  {
    id: "breadcrumbs",
    name: "Breadcrumbs",
    description: "Navigation breadcrumbs",
    icon: "List",
    color: "text-teal-600 bg-teal-100",
    fields: [
      {
        key: "breadcrumbs", label: "Breadcrumb Items", type: "array",
        arrayItemLabel: "Item",
        arrayFields: [
          { key: "name", label: "Name", type: "text", required: true, placeholder: "Home" },
          { key: "url", label: "URL", type: "url", placeholder: "https://example.com" },
        ],
      },
    ],
    buildJsonLd: buildBreadcrumbs,
  },
  {
    id: "jobposting",
    name: "Job Posting",
    description: "Job listings",
    icon: "Briefcase",
    color: "text-amber-600 bg-amber-100",
    fields: [
      { key: "title", label: "Job Title", type: "text", required: true, placeholder: "Senior Software Engineer" },
      { key: "description", label: "Job Description", type: "textarea", required: true, placeholder: "We are looking for..." },
      { key: "datePosted", label: "Date Posted", type: "date", required: true },
      { key: "validThrough", label: "Application Deadline", type: "date" },
      { key: "companyName", label: "Company Name", type: "text", required: true, placeholder: "Acme Corp" },
      { key: "companyUrl", label: "Company URL", type: "url", placeholder: "https://example.com" },
      {
        key: "employmentType", label: "Employment Type", type: "select",
        options: [
          { value: "FULL_TIME", label: "Full Time" },
          { value: "PART_TIME", label: "Part Time" },
          { value: "CONTRACTOR", label: "Contractor" },
          { value: "TEMPORARY", label: "Temporary" },
          { value: "INTERN", label: "Intern" },
        ],
      },
      { key: "locationCity", label: "Location City", type: "text", placeholder: "San Francisco" },
      { key: "locationState", label: "Location State", type: "text", placeholder: "CA" },
      { key: "locationCountry", label: "Location Country", type: "text", placeholder: "US" },
      {
        key: "remote", label: "Remote Work", type: "select",
        options: [
          { value: "false", label: "On-site" },
          { value: "true", label: "Remote" },
        ],
      },
      { key: "salaryMin", label: "Salary Min", type: "number", placeholder: "80000" },
      { key: "salaryMax", label: "Salary Max", type: "number", placeholder: "120000" },
      { key: "salaryCurrency", label: "Salary Currency", type: "text", placeholder: "USD" },
      {
        key: "salaryUnit", label: "Salary Period", type: "select",
        options: [
          { value: "YEAR", label: "Per Year" },
          { value: "MONTH", label: "Per Month" },
          { value: "HOUR", label: "Per Hour" },
        ],
      },
    ],
    buildJsonLd: buildJobPosting,
  },
  {
    id: "service",
    name: "Service",
    description: "Professional services",
    icon: "Wrench",
    color: "text-cyan-600 bg-cyan-100",
    fields: [
      { key: "name", label: "Service Name", type: "text", required: true, placeholder: "Web Development" },
      { key: "description", label: "Description", type: "textarea", placeholder: "Description of the service" },
      { key: "url", label: "Service URL", type: "url", placeholder: "https://example.com/service" },
      { key: "serviceType", label: "Service Type", type: "text", placeholder: "Web Development" },
      { key: "areaServed", label: "Area Served", type: "text", placeholder: "New York, NY" },
      { key: "providerName", label: "Provider Name", type: "text", required: true, placeholder: "Acme Corp" },
      { key: "providerUrl", label: "Provider URL", type: "url", placeholder: "https://example.com" },
      {
        key: "providerType", label: "Provider Type", type: "select",
        options: [
          { value: "Organization", label: "Organization" },
          { value: "Person", label: "Person" },
        ],
      },
      { key: "price", label: "Starting Price", type: "number", placeholder: "499" },
      { key: "currency", label: "Currency", type: "text", placeholder: "USD" },
      { key: "ratingValue", label: "Rating (1-5)", type: "number", placeholder: "4.8" },
      { key: "reviewCount", label: "Review Count", type: "number", placeholder: "50" },
    ],
    buildJsonLd: buildService,
  },
];
